import React from "react";
import { AlertTriangle, RotateCw, Mail } from "lucide-react";

// Class component: React's error-boundary API (componentDidCatch /
// getDerivedStateFromError) has no hooks equivalent, so this is one of the
// few places a class is still required. Catches rendering/lifecycle errors
// anywhere below it in the tree and shows a styled fallback instead of a
// blank white screen — it does NOT catch errors in event handlers, async
// code, or the boundary's own render, by React's design.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Unhandled UI error:", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-[#050914] flex flex-col items-center justify-center px-4">
        <div className="absolute inset-0 pool-tiles opacity-10 pointer-events-none" />

        <div className="relative w-full max-w-md">
          <div className="absolute inset-0 rounded-2xl bg-[#FF3B30]/5 blur-2xl pointer-events-none" />

          <div className="relative bg-[#0A1128]/90 backdrop-blur-xl border border-[#FF3B30]/20 rounded-2xl p-8 md:p-10 text-center shadow-[0_0_60px_rgba(255,59,48,0.08)]">
            <div className="w-16 h-16 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-[#FF3B30]" />
            </div>

            <div className="font-mono text-xs tracking-[0.4em] text-[#FF3B30]/60 uppercase mb-2">
              Something Went Wrong
            </div>

            <h1 className="font-display font-black text-3xl text-white mb-3">
              Unexpected Error
            </h1>

            <p className="text-[#64748B] text-sm leading-relaxed mb-6">
              Something broke on our end. Reloading the page usually fixes it — if it keeps happening, let us know.
            </p>

            <div className="bg-[#050914]/60 border border-[#1E293B] rounded-xl p-4 mb-7 text-left">
              <p className="text-xs text-[#475569] mb-2 uppercase tracking-wider">Still stuck?</p>
              <a
                href="mailto:support@asianmastersbangalore2026.com"
                className="flex items-center gap-2 text-sm text-[#00F0FF] hover:underline"
              >
                <Mail className="w-4 h-4 shrink-0" />
                support@asianmastersbangalore2026.com
              </a>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/15 text-sm font-semibold transition-colors"
            >
              <RotateCw className="w-4 h-4" /> Reload Page
            </button>
          </div>

          <p className="text-center text-xs text-[#334155] mt-6">
            AMSC India 2026 · Bengaluru
          </p>
        </div>
      </div>
    );
  }
}
